import React, { useEffect } from 'react';
import {
   Box,
   IconButton,
   Typography,
   Fade,
   CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import type { GalleryPhoto } from './GalleryGrid';
import { VideoWithLoadingOverlay } from '../public-gallery/VideoWithLoadingOverlay';
import { MediaUploaderAttribution, hasUploaderMediaIds } from '../common/MediaUploaderAttribution';

const isVideo = (p: GalleryPhoto) => p?.mediaType === 'video';

export interface PhotoLightboxProps {
   photos: GalleryPhoto[];
   currentIndex: number;
   onClose: () => void;
   onNavigate: (index: number) => void;
   onDownload?: (photo: GalleryPhoto) => void;
   onDelete?: (photo: GalleryPhoto) => void;
   downloadingPhotoIds?: Set<string>;
   canDelete?: boolean;
   onUploaderClick?: (photo: GalleryPhoto) => void;
}

export default function PhotoLightbox({
   photos,
   currentIndex,
   onClose,
   onNavigate,
   onDownload,
   onDelete,
   downloadingPhotoIds,
   canDelete,
   onUploaderClick,
}: PhotoLightboxProps) {
   const photo = photos[currentIndex];
   const isDownloading = !!photo && !!downloadingPhotoIds?.has(photo.id);

   useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
         switch (e.key) {
            case 'ArrowRight':
               onNavigate(Math.min(currentIndex + 1, photos.length - 1));
               break;
            case 'ArrowLeft':
               onNavigate(Math.max(currentIndex - 1, 0));
               break;
            case 'Escape':
               onClose();
               break;
         }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, [currentIndex, photos.length, onClose, onNavigate]);

   if (!photo) return null;

   return (
      <Fade in>
         <Box
            onClick={onClose}
            sx={{
               position: 'fixed',
               inset: 0,
               zIndex: 1300,
               bgcolor: 'rgba(0,0,0,0.95)',
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
            }}
         >
            {/* Top bar: Download, Delete (if canDelete), Close */}
            <Box
               sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  right: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                  zIndex: 2,
               }}
            >
               {onDownload && photo && (
                  <IconButton
                     onClick={(e) => {
                        e.stopPropagation();
                        onDownload(photo);
                     }}
                     disabled={isDownloading}
                     sx={{
                        color: 'white',
                        bgcolor: 'rgba(0, 196, 167, 0.9)',
                        '&:hover': { bgcolor: '#00C4A7' },
                        '&.Mui-disabled': { bgcolor: 'rgba(0, 196, 167, 0.5)', color: 'white' },
                     }}
                     size='medium'
                     aria-label='Download photo'
                     title='Download photo'
                  >
                     {isDownloading ? (
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                     ) : (
                        <DownloadIcon />
                     )}
                  </IconButton>
               )}
               {canDelete && onDelete && photo && (
                  <IconButton
                     onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                        onDelete(photo);
                     }}
                     sx={{
                        color: 'white',
                        bgcolor: 'rgba(211, 47, 47, 0.9)',
                        '&:hover': { bgcolor: '#d32f2f' },
                     }}
                     size='medium'
                     aria-label='Remove photo from album'
                     title='Remove photo from album'
                  >
                     <DeleteIcon />
                  </IconButton>
               )}
               <IconButton
                  onClick={(e) => {
                     e.stopPropagation();
                     onClose();
                  }}
                  sx={{
                     color: 'white',
                     bgcolor: 'rgba(0,0,0,0.5)',
                     '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                  size='large'
                  aria-label='Close'
               >
                  <CloseIcon />
               </IconButton>
            </Box>

            {/* Left arrow - fixed on left edge for easy navigation */}
            <IconButton
               onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(Math.max(currentIndex - 1, 0));
               }}
               disabled={currentIndex <= 0}
               sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  zIndex: 2,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  '&.Mui-disabled': { color: 'grey.500', bgcolor: 'rgba(0,0,0,0.2)' },
               }}
               size='large'
               aria-label='Previous photo'
            >
               <ChevronLeftIcon sx={{ fontSize: 40 }} />
            </IconButton>

            {/* Center media - video or image; click does not close (only backdrop or X closes) */}
            <Box onClick={(e) => e.stopPropagation()} sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
               {isVideo(photo) ? (
                  <VideoWithLoadingOverlay
                     key={currentIndex}
                     src={photo.url || (photo as { videoUrl?: string }).videoUrl || ''}
                     poster={photo.thumbnailUrl || undefined}
                     fillContainer={false}
                     style={{
                        maxWidth: '90vw',
                        maxHeight: '85vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                     }}
                  />
               ) : (
                  <Box
                     component='img'
                     src={photo.url || photo.thumbnailUrl}
                     alt={photo.title || ''}
                     sx={{
                        maxWidth: '90vw',
                        maxHeight: '85vh',
                        objectFit: 'contain',
                     }}
                  />
               )}
            </Box>

            {/* Right arrow - fixed on right edge for easy navigation */}
            <IconButton
               onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(Math.min(currentIndex + 1, photos.length - 1));
               }}
               disabled={currentIndex >= photos.length - 1}
               sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  zIndex: 2,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  '&.Mui-disabled': { color: 'grey.500', bgcolor: 'rgba(0,0,0,0.2)' },
               }}
               size='large'
               aria-label='Next photo'
            >
               <ChevronRightIcon sx={{ fontSize: 40 }} />
            </IconButton>

            {hasUploaderMediaIds(photo.userId, photo.uploadedBy) && (
               <Box
                  sx={{
                     position: 'absolute',
                     bottom: 56,
                     left: '50%',
                     transform: 'translateX(-50%)',
                     zIndex: 4,
                     maxWidth: 'min(92vw, 480px)',
                  }}
                  onClick={(e) => e.stopPropagation()}
               >
                  <MediaUploaderAttribution
                     variant='lightboxBar'
                     userId={photo.userId || photo.uploadedBy}
                     onClick={
                        onUploaderClick && (photo.userId || photo.uploadedBy)
                           ? () => onUploaderClick(photo)
                           : undefined
                     }
                     avatarSize={40}
                  />
               </Box>
            )}

            <Typography
               variant='body2'
               sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'grey.400',
               }}
            >
               {currentIndex + 1} / {photos.length}
            </Typography>
         </Box>
      </Fade>
   );
}
